import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["label", "routeTo", "children", "defaultSelected", "disabled"],
      _excluded2 = ["target", "url", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { cloneElement, useContext, useEffect, useRef } from "react";
import { LINK, MEGAMENU, NAVBARITEM, NAVLINK } from "../../common/constants";
import { useUniqueId } from "../../common/hooks";
import { invalidIf, requiredIf } from "../../common/proptypes";
import { OVERRIDE } from "../../themes/common/classes";
import { Anchor, Button, IconArrowClose, IconArrowOpen, IconForward } from "../..";
import { NavBarContext } from "../NavBarContext";
import extractTheme from "./extractTheme";
/**
 * NavBarItem is a React component that renders a button or anchor.
 * NavBarItem can only be used as a child of NavBar component.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function NavBarItem(_ref) {
  let {
    label,
    routeTo,
    children,
    defaultSelected,
    disabled
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const megaMenuRef = useRef(null);
  const navItemButtonRef = useRef();
  const theme = useTheme();
  const {
    navItemsDropDown,
    selectedNavItem,
    handleSelectedNavItem,
    setShowMegaMenu,
    showMegaMenu
  } = useContext(NavBarContext);
  const isMegaMenuOpen = selectedNavItem === label && showMegaMenu;
  const {
    cssRoot,
    megaMenuRoot,
    item,
    userPassedComponentElement,
    icon
  } = extractTheme({
    theme,
    navItemsDropDown,
    label,
    selectedNavItem
  });
  const uniqueId = useUniqueId();
  /**
   * Whenever application team changes the `defaultSelected` this
   * effect would execute and set the selected styles to respective NavBarItem.
   */

  useEffect(() => {
    defaultSelected && handleSelectedNavItem(label);
    /**
     * Disabled warning to add `handleSelectedNavItem` as dependency.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected, label]);
  /**
   * Set/Unset the `selectedNavItem` state in NavBar component.
   * If `label` is equal to `selectedNavItem` that means MegaMenu is already
   * open so closing the MegaMenu by setting `false` to `selectedNavItem`.
   * If other NavBarItem is clicked update `selectedNavItem` to that value
   * and setShowMegaMenu to `true` if `MegaMenu` is present.
   */

  const handleNavItemClick = () => {
    handleSelectedNavItem(label);

    if (label === selectedNavItem) {
      setShowMegaMenu(!showMegaMenu);
    } else {
      setShowMegaMenu(Boolean(children));
    }
  };
  /**
   * Set the `selectedNavItem` state in NavBar component.
   * When A11Y user revisits NavBar while navigating using ArrowKeys
   * selected NavBarItem index need to be set via focus event so
   * that index of focused NavBarItem is synced with NavBar component.
   */


  const handleNavItemFocus = () => {
    handleSelectedNavItem(label);
  };
  /**
   * This method validates the `routeTo` prop and returns a renderable component.
   * If a component is being sent we clone the element and apply V4 styles to it.
   * As NavBarItem has an icon during dropdown view, add it as children to user
   * passed component.
   * @returns An V4 Anchor component or user passed component(NavLink, Link).
   */


  const nonMegaMenuLink = () => {
    var _routeTo$type;

    const routeToType = typeof routeTo;
    let itemProps = {
      fullWidth: true,
      buttonStyles: item,
      disabled,
      // Render icon only during dropdown view.
      rightIcon: navItemsDropDown && _jsx(IconForward, {
        size: "xsmall",
        decorative: true
      }),
      smoothBoldLabelTransition: true
    };
    const isObjectType = routeToType === "object" && !((_routeTo$type = routeTo.type) !== null && _routeTo$type !== void 0 && _routeTo$type.displayName);

    if (isObjectType) {
      const {
        target = "_self",
        url,
        onClick
      } = routeTo,
            otherRouteToProps = _objectWithoutProperties(routeTo, _excluded2);

      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        target,
        href: url,
        tabIndex: disabled ? -1 : 0,
        onClick: onClick && (e => onClick(e, _objectSpread({
          target,
          url
        }, otherRouteToProps)))
      }, otherRouteToProps);
    } else if (routeToType === "string") {
      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        href: routeTo
      });
    }

    if (routeToType === "string" || isObjectType) {
      return _jsx(Anchor, _objectSpread(_objectSpread({}, itemProps), {}, {
        children: label
      }));
    } else {
      return /*#__PURE__*/cloneElement(routeTo, {
        style: userPassedComponentElement,
        className: "pnr-base-button pnr-navitem"
      }, [routeTo.props.children, // Render icon only during dropdown view.
      navItemsDropDown && _jsx(IconForward, {
        style: icon,
        size: "xsmall",
        decorative: true
      }, uniqueId)]);
    }
  };
  /**
   * This method is used to change the icon displayed in NavBarItem during
   * dropdown view. Icon is changed based the NavBarItem's MegaMenu status.
   * @returns An Icon based on MegaMenu's visibility of a NavBarItem.
   */


  const navItemButtonIcon = () => isMegaMenuOpen ? _jsx(IconArrowOpen, {
    size: "xsmall",
    decorative: true
  }) : _jsx(IconArrowClose, {
    size: "xsmall",
    decorative: true
  });

  const renderNavItem = () =>
  /**
   * If `routeTo` prop is passed then it's an clickable link else it is a
   * button that has MegaMenu.
   */
  routeTo ? nonMegaMenuLink() : _jsx(Button, {
    "aria-expanded": isMegaMenuOpen,
    "aria-haspopup": true,
    buttonStyles: item,
    disabled: disabled,
    fullWidth: true,
    kind: "simple",
    label: label,
    rightIcon: navItemsDropDown && navItemButtonIcon(),
    smoothBoldLabelTransition: true
  });

  return _jsxs("li", _objectSpread(_objectSpread({
    className: OVERRIDE,
    ref: navItemButtonRef,
    css: cssRoot,
    onClick: handleNavItemClick,
    onFocus: handleNavItemFocus
  }, otherProps), {}, {
    children: [renderNavItem(), children && selectedNavItem === label && showMegaMenu && _jsx("div", {
      ref: megaMenuRef,
      css: megaMenuRoot,
      children: children
    })]
  }));
}

NavBarItem.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Children to be displayed when NavBarItem is selected.
   * Children can only be MegaMenu component.
   */
  children: invalidIf(PropTypes.node, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== MEGAMENU) {
        error = true;
      }
    });
    return error;
  }, `'NavBarItem' children should be of type '${MEGAMENU}'.`),

  /**
   * This prop adds selected styles on load to the NavBarItem.
   * Useful when the landing page is navigable NavBarItem.
   */
  defaultSelected: PropTypes.bool,
  // To disable a NavBarItem and prevent any action on it.
  disabled: PropTypes.bool,

  /**
   * Label to be displayed in NavBarItem component.
   * Label is required if `routeTo` prop is of type `string` or `object`.
   */
  label: requiredIf(PropTypes.string, props => !props.label && typeof props.routeTo !== "object", "when 'routeTo' is of type string or object."),

  /**
   * It can be a string or object or `NavLink` or Link` component.
   * Required if `children` prop is not present.
   */
  routeTo: function (props, propName, componentName) {
    let error = "";
    const prop = props[propName];
    const propType = PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.shape({
      url: requiredIf(PropTypes.string, props => !props.url && !props.onClick, "when `onClick` is not passed"),
      target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"]),
      onClick: PropTypes.func
    })]);

    if (typeof prop === "object" && prop) {
      var _prop$type;

      const displayName = (_prop$type = prop.type) === null || _prop$type === void 0 ? void 0 : _prop$type.displayName;

      if ((displayName || prop.type) && displayName !== LINK && displayName !== NAVLINK) {
        error = `'routeTo' prop should be of type 'string' or 'object' or accepts only 'Link' or 'NavLink' as components.`;
      }
    } else if (!props.routeTo && !props.children) {
      error = `'routeTo' prop is required if 'children' is not passed to 'NavBarItem'.`;
    }

    for (var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
      rest[_key - 3] = arguments[_key];
    }

    return error ? new Error(error) : propType(props, propName, componentName, ...rest);
  }
} : {};
NavBarItem.defaultProps = {
  children: null,
  defaultSelected: false,
  disabled: false,
  label: "",
  routeTo: ""
};
NavBarItem.displayName = NAVBARITEM;
export default NavBarItem;